/* ═══════════════════════════════════════════════════════════════════════════
   js/plan-modal.js — окно крупного плана планировки. Префикс классов `zpo-`.

   Владелец 20.08.2026: «когда нажимаешь сводку, показать планировки — они
   открываются, а нажать я на них не могу; нужно, чтоб либо модальное окно
   открывалось, либо ещё что-то». Выбран вариант с окном.

   Человек раскрывал сводку, видел чертёж — и упирался в стену: плитка не
   нажималась. Теперь клик открывает крупный план с числами этой планировки и
   двумя выходами: посмотреть все такие квартиры или оставить заявку.

   ДВЕ СТРАНИЦЫ, ОДИН ФАЙЛ:
     • витрина квартир (public/flats.php) — плитки `.zkv-fs-card`, их рисует
       js/flats-showcase.js (planTile);
     • карточка ЖК (public/jk.php), вид «Сводка» — плитки `.fs-card`, их рисует
       js/jk-filter-results.js (summaryTile).
   Обе кладут на плитку один и тот же набор `data-zpo-*`, поэтому окно не знает,
   на какой оно странице, и вторую реализацию заводить не пришлось.

   ⛔ ЧИСЛА ЧИТАЕМ ИЗ data-АТРИБУТОВ ПЛИТКИ, а не разбираем обратно её текст.
      «20,7–24,1 м²» назад в числа — это второй формат одних величин, и он
      разойдётся с первым на первой же планировке без цены.

   ⛔ «Смотреть N квартир» ведёт ФИЛЬТРОМ ПО ПЛОЩАДИ, а не на одну квартиру: у
      планировки обычно десятки квартир на разных этажах и с разной ценой, и
      вести на одну наугад — обманывать.
   ═══════════════════════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  /* Плитки, которые открывают окно. Классы перечислены явно: общий
     предок-селектор («любая плитка в сводке») цеплял бы и будущие блоки, у
     которых набора data-zpo-* нет, — окно открывалось бы пустым.
       .zkv-fs-card — плитка витрины квартир (js/flats-showcase.js);
       .fs-card     — плитка «Сводки» карточки ЖК (js/jk-filter-results.js);
       .jk-filter__plan-cover — ЧЕРТЁЖ карточки вида «Планировки» (там же).
     Третий добавлен 28.08.2026: владелец принял вид, где клик по чертежу
     открывает крупный план, а клик по остальной карточке раскрывает панель
     квартир. Одно нажатие — одно действие: onCardClick в jk-filter-results.js
     первой строкой выходит, если попали в обложку. */
  const TILE_SEL = '.zkv-fs-card, .fs-card, .jk-filter__plan-cover';

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  const ico = (name) => {
    const i = document.createElement('i');
    i.className = 'fa-solid ' + name;
    i.setAttribute('aria-hidden', 'true');
    return i;
  };

  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  /* ⛔ ОКРУГЛЕНИЕ ТО ЖЕ, ЧТО У ПЛИТКИ. Числа окна обязаны совпасть с числами
     плитки, на которую человек только что нажал, — иначе он видит две правды
     об одной планировке. Диапазон схлопываем ПО ДВУМ условиям сразу:
       • сырые границы равны — так решает витрина квартир (flats-showcase);
       • напечатанные границы совпали — так решает карточка ЖК (areaRangeText).
     Взять только первое значило разойтись с карточкой ЖК, только второе — с
     витриной. Вместе они совпадают с обеими, а лишним побочным следствием
     остаётся лишь то, что окно не печатает вырожденное «20,7–20,7».
     Запятая, а не точка: во всей витрине дробная часть через запятую. */
  const fmtArea = (v) => String(Math.round(v * 10) / 10).replace('.', ',');
  /* ⛔ ПЕРЕД «₽» — НЕРАЗРЫВНЫЙ ПРОБЕЛ. Замер на 1920 (28.08.2026): колонка фактов
     340 px, «11 582 510 ₽» не влезала в строку, и рубль уезжал вниз — число
     читалось как две разные величины. Разряды Intl уже ставит неразрывными
     (U+00A0), а вот пробел перед знаком валюты ставили мы, обычный.
     Оформление подстраховано в css/plan-modal.css (`.zpo-fact-v`), но чинить
     это ТОЛЬКО оформлением нельзя: та же строка уходит в текст оговорки
     «Цена зависит от этажа…», где перенос ничем не запрещён. */
  const fmtPrice = (v) => new Intl.NumberFormat('ru-RU').format(Math.round(v)) + '\u00a0₽';

  const plural = (n, a, b, c) => {
    const x = Math.abs(n) % 100, y = x % 10;
    if (x > 10 && x < 20) return c;
    if (y > 1 && y < 5) return b;
    if (y === 1) return a;
    return c;
  };

  /* ── Адрес чертежа ────────────────────────────────────────────────────────
     ⛔ ПРАВИЛА УМЕНЬШИТЕЛЯ ПОВТОРЯЮТ public/js/catalog.js и
     public/js/flats-showcase.js ДОСЛОВНО, и это не вкусовщина: /img-thumb.php
     кэширует по СТРОКЕ запроса. Своё, «попроще», кодирование (без RFC-3986 для
     `!'()*`, без снятия собственного хоста, без ветки /img/building_renders)
     дало бы второй ключ кэша на тот же кадр — то есть вторую конвертацию,
     второй файл на диске и промах кэша у каждого, кто уже видел эту плитку.
     Совпадение с flats-showcase.js закреплено тестом (plan-modal.test.js). */
  const LOCAL_THUMBABLE = /^\/img\/building_renders\/[\w./-]+\.(?:jpe?g|png|webp)$/i;
  const rawUrlEncode = (s) => encodeURIComponent(s)
    .replace(/[!'()*]/g, (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase());

  /* Паритет с safeSrc() карточки ЖК и PHP-политикой LayoutPolicy: битая запись
     фида не должна дать `javascript:` в src или чужой хост с userinfo. */
  const safeSrc = (raw) => {
    const s = String(raw == null ? '' : raw).trim();
    if (/^\/[^/\\]/.test(s)) return s;
    if (/^\/\/[^/\\]/.test(s)) return 'https:' + s;
    if (!/^https?:\/\//i.test(s)) return '';
    const authority = s.replace(/^https?:\/\//i, '').split(/[/?#]/)[0];
    return authority.indexOf('@') === -1 ? s : '';
  };

  function thumbUrl(src, w) {
    let url = safeSrc(src);
    if (url === '') return '';
    url = url.replace(/^https?:\/\/(?:www\.)?bronidom\.ru(?=\/)/i, '');
    const width = w || 1200;
    if (/^https?:\/\//i.test(url)) return '/img-thumb.php?src=' + rawUrlEncode(url) + '&w=' + width;
    if (LOCAL_THUMBABLE.test(url) && url.indexOf('..') < 0) {
      return '/img-thumb.php?src=' + rawUrlEncode(url) + '&w=' + width;
    }
    return url; // не картинка фида (svg-заглушка, чужой путь) — отдаём как есть
  }

  /* ── Адрес выдачи «Смотреть N квартир» ────────────────────────────────────
     ⛔ ТЕКУЩИЙ АДРЕС ГОДИТСЯ ТОЛЬКО НА ВИТРИНЕ КВАРТИР: там плитка и выдача
     живут на одной странице. На карточке ЖК `location.pathname` — это сама
     карточка, и ссылка вела бы обратно на неё же. Поэтому карточка ЖК кладёт
     на плитку `data-zpo-base` — ЧПУ страницы квартир ЭТОГО дома.

     ⛔⛔ ГОРОДСКАЯ ВИТРИНА ТРЕБУЕТ ПРИВЯЗКИ К ДОМУ — ПРИЁМКА 21.08.2026.
     На `/novostroyki/<город>/kvartiry/` плитки рисует та же витрина, но
     `data-zpo-base` там нет: страница и выдача совпадают, и адрес собирался
     от `location.pathname`. Плитка при этом принадлежит КОНКРЕТНОМУ дому
     («Тёплые края»), а получался общегородской фильтр:

       кнопка обещает      «Смотреть 12 квартир»
       ?rooms=0&area_min=20.7&area_max=21.1   → на странице 97

     То есть кнопка врала числом в восемь раз. Замер прода подтвердил обе
     цифры. Чинится добавлением `building_id` дома из самой плитки: тот же
     адрес с `&building_id=24` даёт ровно 12.

     ⛔ ПОЧЕМУ НЕ `/flats.php?city=…&building_id=…`: замер показал, что этот
     адрес канонизируется на ЧПУ дома и ТЕРЯЕТ по дороге rooms/area_min/
     area_max — выдача становится «все 1 417 квартир дома». Приписка
     `building_id` к текущему пути такого сноса не вызывает.

     ⛔ ДОМА НЕ ЗНАЕМ — ССЫЛКИ НЕ РИСУЕМ. Это уже правило соседней ветки
     («дом без адреса витрины — ссылку не рисуем, а не ведём в никуда»), и
     здесь оно ровно то же: без привязки число на кнопке снова разойдётся с
     числом на целевой странице, а молча соврать хуже, чем не позвать. */
  const isSafePath = (p) => /^\/(?!\/)/.test(p);

  /** Дом, к которому уже привязана САМА страница (её печатает сервер). */
  const pageBuildingId = () => String(window.BRONI_FLATS_BUILDING_ID || '').trim();

  function targetUrl(d, aMin, aMax) {
    const base = String(d.zpoBase == null ? '' : d.zpoBase).trim();
    const scoped = 'zpoBase' in d; // плитку рисует не витрина квартир
    let path = '';
    let params;
    if (base !== '' && isSafePath(base)) {
      // Адрес приходит от сервера готовым; свой query у него бывает только у
      // легаси-ветки (/flats.php?building_id=N) — её сохраняем.
      const cut = base.indexOf('?');
      path = cut === -1 ? base : base.slice(0, cut);
      params = new URLSearchParams(cut === -1 ? '' : base.slice(cut + 1));
    } else if (scoped) {
      return ''; // дом без адреса витрины — ссылку не рисуем, а не ведём в никуда
    } else {
      path = window.location.pathname;
      params = new URLSearchParams(window.location.search);

      // Страница уже про один дом (ЧПУ дома) — привязка есть, добавлять нечего.
      if (pageBuildingId() === '') {
        const jkId = String(d.zpoJkId == null ? '' : d.zpoJkId).trim();
        // Только целое: `building_id` уходит в адрес, а данные приходят из фидов.
        if (!/^[1-9]\d{0,9}$/.test(jkId)) return '';
        params.set('building_id', jkId);
      }
    }

    if (d.zpoRooms != null && d.zpoRooms !== '') params.set('rooms', d.zpoRooms);
    /* Границы округляем НАРУЖУ на 0,1 м²: сервер сравнивает по точным числам, и
       ровное «20,7» рискует отсечь саму планировку. */
    if (aMin) params.set('area_min', String(Math.floor(aMin * 10) / 10));
    if (aMax || aMin) params.set('area_max', String(Math.ceil((aMax || aMin) * 10) / 10));
    /* ⛔ `view` СНИМАЕМ, а не ставим. Черновик писал `view=list`, но витрина
       знает из адреса только `view=plans`; «list» молча падал в вид из
       localStorage. Снятие честно возвращает человека к виду по умолчанию —
       и не тащит в индексируемый адрес параметр, который ничего не значит. */
    params.delete('view');
    params.delete('page');

    const qs = params.toString();
    return qs === '' ? path : path + '?' + qs;
  }

  /* Секции фида планировки из `data-zpo-sections` (JSON-список строк, кладёт
     js/jk-filter-results.js). Битое значение — пустой список, а не исключение. */
  const readSections = (raw) => {
    if (!raw) return [];
    try {
      const list = JSON.parse(raw);
      return Array.isArray(list) ? list.filter((s) => typeof s === 'string' && s !== '') : [];
    } catch (e) {
      return [];
    }
  };

  let win = null;
  let lastFocus = null;

  /* ── Замок фона ───────────────────────────────────────────────────────────
     ⛔⛔ КЛАСС НА <html>, А НЕ ТОЛЬКО НА <body> — ПРИЁМКА 21.08.2026.
     `.zpo-lock{overflow:hidden}` висел на одном `<body>` и не держал ничего:
     замер настоящим касанием под открытым окном дал 0 → 485 → 870 px.

     Причина в каскаде страницы: в main.css стоит `html{overflow-x:clip}`, то
     есть у корня overflow равен `clip visible` — НЕ `visible`. А правило
     браузера такое: overflow берётся у <body> ТОЛЬКО когда у <html> он
     полностью `visible`. Здесь это не так, значит настоящий скроллер — сам
     `documentElement`, и запирать надо его. Классу на <body> браузер при
     этом молча не даёт никакого действия: ни ошибки, ни следа.

     Класс вешаем на ОБА узла: на корень — чтобы замок работал, на тело —
     чтобы он продолжал работать, если правило `overflow-x:clip` из main.css
     когда-нибудь уйдёт и скроллером снова станет <body>.

     ⛔ ПОЛОСУ ПРОКРУТКИ КОМПЕНСИРУЕМ. `overflow:hidden` на корне убирает
     полосу, и на компьютере вся страница (вместе с закреплённой шапкой)
     прыгает вправо на её ширину. «Числа сошлись, а вид нет» — ровно тот
     случай; поэтому на время замка отдаём ту же ширину отступом. */
  const lockBg = (on) => {
    const root = document.documentElement;
    if (on) {
      const gap = window.innerWidth - root.clientWidth;
      root.style.setProperty('--zpo-sbw', (gap > 0 ? gap : 0) + 'px');
    }
    root.classList.toggle('zpo-lock', on);
    document.body.classList.toggle('zpo-lock', on);
    if (!on) root.style.removeProperty('--zpo-sbw');
  };

  const close = () => {
    if (!win || win.hidden) return;
    win.hidden = true;
    lockBg(false);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };

  const build = () => {
    if (win) return win;
    win = el('div', 'zpo-win');
    win.hidden = true;

    const veil = el('div', 'zpo-veil');
    veil.addEventListener('click', close);
    win.appendChild(veil);

    const box = el('div', 'zpo-box');
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Планировка');
    box.tabIndex = -1;

    const x = el('button', 'zpo-x');
    x.type = 'button';
    x.setAttribute('aria-label', 'Закрыть');
    x.appendChild(ico('fa-xmark'));
    x.addEventListener('click', close);
    box.appendChild(x);

    box.appendChild(el('div', 'zpo-img'));
    box.appendChild(el('div', 'zpo-side'));
    win.appendChild(box);
    document.body.appendChild(win);

    // Escape закрывает — окно поверх всего, и мышью до крестика ещё надо дойти.
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
    return win;
  };

  const open = (card) => {
    const d = card.dataset;
    build();
    lastFocus = card;

    /* Половина плана могла быть снята прошлым открытием (битый чертёж) —
       собираем коробку заново, а не чиним остатки. */
    const box = win.querySelector('.zpo-box');
    let imgBox = win.querySelector('.zpo-img');
    if (!imgBox) {
      imgBox = el('div', 'zpo-img');
      box.insertBefore(imgBox, win.querySelector('.zpo-side'));
    }
    const side = win.querySelector('.zpo-side');
    imgBox.textContent = '';
    side.textContent = '';

    // ── крупный план ──
    const src = thumbUrl(d.zpoSrc, 1200);
    if (src !== '') {
      const img = document.createElement('img');
      img.className = 'zpo-img-el';
      img.alt = 'Планировка ' + (d.zpoLabel || '');
      img.decoding = 'async';
      /* План не загрузился — окно не должно показывать пустую рамку размером в
         пол-экрана: убираем половину, числа остаются. Подписка ДО присвоения
         src: у картинки из кэша событие успевает прийти раньше обработчика. */
      img.addEventListener('error', () => { imgBox.remove(); });
      img.src = src;
      imgBox.appendChild(img);
    } else {
      imgBox.remove();
    }

    // ── правая половина: числа ──
    if (d.zpoLabel) side.appendChild(el('h3', 'zpo-ttl', d.zpoLabel));
    if (d.zpoJk) side.appendChild(el('div', 'zpo-jk', d.zpoJk));

    const facts = el('div', 'zpo-facts');
    const fact = (val, lbl) => {
      if (!val) return;
      const f = el('div', 'zpo-fact');
      f.appendChild(el('span', 'zpo-fact-v', val));
      f.appendChild(el('span', 'zpo-fact-l', lbl));
      facts.appendChild(f);
    };

    const aMin = num(d.zpoAreaMin), aMax = num(d.zpoAreaMax);
    if (aMin) {
      const lo = fmtArea(aMin);
      const hi = aMax && aMax > aMin ? fmtArea(aMax) : lo;
      fact((hi === lo ? lo : lo + '–' + hi) + ' м²', 'Площадь');
    }
    const pMin = num(d.zpoPriceMin), pMax = num(d.zpoPriceMax);
    // ⛔ Ноль — не цена: планировка без цены не должна обещать бесплатную квартиру.
    if (pMin && pMin > 0) fact(fmtPrice(pMin), pMax && pMax > pMin ? 'Цена от' : 'Цена');
    const fMin = num(d.zpoFloorMin), fMax = num(d.zpoFloorMax);
    if (fMin) fact((fMax && fMax > fMin ? fMin + '–' + fMax : String(fMin)) + ' эт.', 'Этажи');
    const cnt = num(d.zpoCount);
    if (cnt) fact(String(cnt), plural(cnt, 'квартира', 'квартиры', 'квартир'));

    if (facts.children.length) side.appendChild(facts);

    if (pMin && pMax && pMax > pMin) {
      side.appendChild(el('p', 'zpo-note',
        'Цена зависит от этажа и корпуса: от ' + fmtPrice(pMin) + ' до ' + fmtPrice(pMax) + '.'));
    }

    // ── выходы ──
    const acts = el('div', 'zpo-acts');

    const href = targetUrl(d, aMin, aMax);
    if (href !== '') {
      const go = el('a', 'zpo-go');
      go.href = href;
      go.dataset.ym = 'plan_modal_flats';
      go.appendChild(ico('fa-arrow-right'));
      go.appendChild(el('span', null, cnt
        ? ('Смотреть ' + cnt + ' ' + plural(cnt, 'квартиру', 'квартиры', 'квартир'))
        : 'Смотреть такие квартиры'));

      /* ⛔ ТАМ, ГДЕ ПАНЕЛЬ ЕСТЬ, КНОПКА РАСКРЫВАЕТ ЕЁ, А НЕ УВОДИТ НА ВИТРИНУ.
         Владелец 27.08: «пусть он открывает снизу развёрнутый вид планировки,
         а не перекидывает на витрину всех квартир».

         Панель есть ровно у одного источника — карточки вида «Планировки»
         (обложка `.jk-filter__plan-cover` внутри кнопки `.jk-filter__plan`).
         У плиток витрины и «Сводки» панели НЕТ, и там кнопка обязана работать
         по-старому — обычной ссылкой на витрину. Поэтому решает не страница, а
         то, ЧЕМ окно открыли: lastFocus.

         ⛔ `href` НЕ ТРОГАЕМ. Средняя кнопка, Ctrl/Cmd, «открыть в новой
            вкладке» и работа без JS обязаны по-прежнему вести на витрину —
            перехватываем ТОЛЬКО обычный левый клик без модификаторов.
         ⛔ ОКНО ЗАКРЫВАЕМ ЕГО ЖЕ close(), а не `hidden = true`: на время показа
            стоит замок прокрутки (класс `zpo-lock` на <html> И <body> плюс
            `--zpo-sbw` под ширину полосы). Спрятать окно мимо close() значит
            оставить страницу навсегда нелистаемой.
         ⛔ ВТОРОГО ПУТИ ОТКРЫТИЯ ПАНЕЛИ НЕ ЗАВОДИМ: нажимаем ту же кнопку
            карточки, что и человек мышью. Тот же список квартир, та же кнопка
            «Назад», та же прокрутка — и ни одной новой ветки, которая могла бы
            разойтись с обычным кликом. Клик по самой кнопке в обложку не
            попадает, поэтому в панель он уходит, а не открывает окно заново. */
      go.addEventListener('click', (e) => {
        /* ⛔ `e.button > 0`, А НЕ `!== 0`. Средняя и правая кнопки дают 1 и 2 и
           обязаны уйти по ссылке. Но у события, созданного без мыши (Event, а не
           MouseEvent — так его шлёт и наш же `card.click()`, и часть тестов),
           поля `button` НЕТ ВОВСЕ, и `undefined !== 0` отсекало бы обычный клик.
           Сравнение «больше нуля» верно в обоих случаях. */
        if (e.defaultPrevented || e.button > 0) return;
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
        const cover = lastFocus && lastFocus.classList
          && lastFocus.classList.contains('jk-filter__plan-cover') ? lastFocus : null;
        if (!cover) return;                       /* панели нет — уходим по ссылке */
        const card = cover.closest ? cover.closest('.jk-filter__plan') : null;
        if (!card || !card.isConnected) return;   /* выдача перерисовалась — тоже по ссылке */
        e.preventDefault();
        close();
        card.click();
      });
      acts.appendChild(go);
    }

    /* ⛔ ВТОРАЯ КНОПКА ВЕДЁТ В БОЕВУЮ МОДАЛКУ ЗАЯВКИ (js/booking-modal.js), А НЕ
       К «воронке» из соседнего черновика. Черновик прокручивал к `.zkw-box` —
       узла с таким классом в проде НЕТ ВОВСЕ (воронка не выкачена), то есть
       кнопка была бы мёртвой: клик закрывал окно и не делал больше ничего.
       booking-modal.js уже стоит в $pageJS ОБЕИХ страниц, у него живой
       consent-гейт 152-ФЗ, маска телефона и настоящая отправка на
       /map_api.php?action=apply — второй формы заявки мы не заводим.
       Нет функции (файл не загрузился) — кнопки просто нет: мёртвая кнопка
       хуже её отсутствия. */
    if (typeof window.openBookingModal === 'function') {
      const ask = el('button', 'zpo-ask');
      ask.type = 'button';
      ask.dataset.ym = 'plan_modal_lead';
      ask.appendChild(ico('fa-phone'));
      ask.appendChild(el('span', null, 'Оставить заявку'));
      ask.addEventListener('click', () => {
        /* Своё окно закрываем ПЕРЕД открытием заявки: booking-modal ставит
           body.overflow сам, и два замка подряд снимались бы по одному. */
        const ctx = {
          building_id: d.zpoJkId,
          building_name: d.zpoJk,
          rooms: d.zpoRooms,
          area: d.zpoAreaMin,
          /* Страница комнатности называет себя (window.BRONI_LEAD_PAGE, flats.php) —
             владелец отличит заявку оттуда от заявки с карточки ЖК (проверка А, P2-2).
             На карточке переменной нет, подпись прежняя. */
          context_label: (typeof window.BRONI_LEAD_PAGE === 'string' && window.BRONI_LEAD_PAGE
            ? window.BRONI_LEAD_PAGE + ' — ' : '')
            + 'Планировка' + (d.zpoLabel ? ' ' + d.zpoLabel : '')
            + (aMin ? ', ' + fmtArea(aMin) + ' м²' : ''),
        };
        close();
        window.openBookingModal(ctx);
      });
      acts.appendChild(ask);
    }

    /* ── «Посмотреть на генплане» (слово владельца 25.09.2026: «добавь только ещё
       подпись „Посмотреть на генплане“») ──
       Только там, где на странице стоит компактный генплан с обводкой корпусов
       (страница комнатности и — с 26.09.2026 — карточка ЖК): js/jk-genplan.js отвечает
       на синхронный вопрос `broni:genplan-find`, какие корпуса схемы у секций этой
       планировки. Генплана с обводкой нет (вопрос никто не слушает) или корпус не
       обведён — подписи нет. Нажатие закрывает окно и поднимает к генплану; у генплана
       появляется «Вернуться к планировке», которая открывает это окно снова. */
    const sections = readSections(d.zpoSections);
    if (sections.length) {
      const ask = { sections: sections.map((section) => ({ section, number: '' })), ids: [] };
      document.dispatchEvent(new CustomEvent('broni:genplan-find', { detail: ask }));
      if (Array.isArray(ask.ids) && ask.ids.length) {
        const show = el('button', 'zpo-genplan jk-genplan-show');
        show.type = 'button';
        show.dataset.hint = 'Покажем на генплане дом, где эта планировка';
        show.dataset.ym = 'plan_modal_genplan';
        show.setAttribute('aria-label', 'Посмотреть на генплане. Покажем дом, где эта планировка');
        show.appendChild(ico('fa-map-location-dot'));
        show.appendChild(el('span', null, 'Посмотреть на генплане'));
        show.addEventListener('click', () => {
          const from = card;
          close();
          document.dispatchEvent(new CustomEvent('broni:genplan-locate', { detail: {
            ids: ask.ids, what: 'планировка', back: 'Вернуться к планировке', from,
            restore: () => { if (from && from.isConnected) open(from); },
          } }));
        });
        acts.appendChild(show);
      }
    }

    if (acts.children.length) side.appendChild(acts);

    win.hidden = false;
    lockBg(true);
    box.focus();
  };

  /* Делегат на документе: плитки сводки рисуются и перерисовываются при каждом
     раскрытии строки, и вешать обработчик на каждую значит терять его при
     следующей отрисовке. */
  document.addEventListener('click', (e) => {
    const card = e.target && e.target.closest ? e.target.closest(TILE_SEL) : null;
    if (!card) return;
    e.preventDefault();
    open(card);
  });

  // С клавиатуры плитка — кнопка: Enter и пробел открывают её.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const card = e.target && e.target.closest ? e.target.closest(TILE_SEL) : null;
    if (!card) return;
    e.preventDefault();
    open(card);
  });
})();
